// RefRating frontend - vanilla JS, no framework (see plan §Frontend rework).
// window.REFRATING_CONFIG is injected by scripts/aws/sync-frontend.ps1 as /config.js.

const config = window.REFRATING_CONFIG || {
  apiBase: "http://localhost:0",
  cognitoRegion: "af-south-1",
  userPoolClientId: "UNCONFIGURED",
};

const AUTH_STORAGE_KEY = "refrating.auth";

/* ---------------- Cognito (direct REST, public app client, no secret) ---------------- */

const cognitoRequest = async (action, body) => {
  const response = await fetch(`https://cognito-idp.${config.cognitoRegion}.amazonaws.com/`, {
    method: "POST",
    headers: {
      "content-type": "application/x-amz-json-1.1",
      "x-amz-target": `AWSCognitoIdentityProviderService.${action}`,
    },
    body: JSON.stringify(body),
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(json.message || json.__type || "Authentication request failed");
  }
  return json;
};

const decodeJwt = (token) => {
  try {
    const payload = token.split(".")[1];
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    return JSON.parse(atob(normalized));
  } catch {
    return {};
  }
};

const auth = {
  session: null,

  load() {
    const raw = sessionStorage.getItem(AUTH_STORAGE_KEY);
    if (!raw) return;
    try {
      this.session = JSON.parse(raw);
    } catch {
      this.session = null;
    }
  },

  save(authResult) {
    const claims = decodeJwt(authResult.IdToken);
    this.session = {
      idToken: authResult.IdToken,
      accessToken: authResult.AccessToken,
      refreshToken: authResult.RefreshToken,
      expiresAt: Date.now() + (authResult.ExpiresIn || 3600) * 1000,
      userId: claims.sub,
      email: claims.email,
      groups: claims["cognito:groups"] || [],
    };
    sessionStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(this.session));
  },

  clear() {
    this.session = null;
    sessionStorage.removeItem(AUTH_STORAGE_KEY);
  },

  isSignedIn() {
    return Boolean(this.session && this.session.expiresAt > Date.now());
  },

  isAdmin() {
    return Boolean(this.session && this.session.groups.includes("admins"));
  },

  async signUp(email, password) {
    await cognitoRequest("SignUp", {
      ClientId: config.userPoolClientId,
      Username: email,
      Password: password,
      UserAttributes: [{Name: "email", Value: email}],
    });
  },

  async confirmSignUp(email, code) {
    await cognitoRequest("ConfirmSignUp", {
      ClientId: config.userPoolClientId,
      Username: email,
      ConfirmationCode: code,
    });
  },

  async signIn(email, password) {
    const result = await cognitoRequest("InitiateAuth", {
      AuthFlow: "USER_PASSWORD_AUTH",
      ClientId: config.userPoolClientId,
      AuthParameters: {USERNAME: email, PASSWORD: password},
    });
    this.save(result.AuthenticationResult);
  },

  async deleteCognitoAccount() {
    // Self-service: Cognito's DeleteUser is authenticated by the caller's
    // own access token, no admin permissions needed on the backend for this.
    await cognitoRequest("DeleteUser", {AccessToken: this.session.accessToken});
  },
};

auth.load();

/* ---------------- API client ---------------- */

const api = async (path, {method = "GET", body, requireAuth = false} = {}) => {
  const headers = {"content-type": "application/json"};
  if (requireAuth) {
    if (!auth.isSignedIn()) throw new Error("Sign in required");
    headers.authorization = `Bearer ${auth.session.idToken}`;
  }
  const response = await fetch(`${config.apiBase}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const json = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(json.error?.message || `Request failed (${response.status})`);
    // Carry the structured error body (code, and anything like
    // existingIncidentId for DUPLICATE_LIKELY) so callers can branch on it
    // instead of just showing the message.
    Object.assign(error, json.error || {});
    error.status = response.status;
    throw error;
  }
  return json;
};

/* ---------------- App state ---------------- */

const state = {
  sports: [],
  currentSport: null,
  rules: [],
  activeRuleIndex: 0,
  incidents: [],
  cursor: undefined,
  disputeFilter: "all",
  sort: "recency",
  myVotes: new Map(), // incidentId -> -1|0|1 (client-side only, see plan note on optional-auth GETs)
  openThreads: new Set(),
  commentsByIncident: new Map(),
  sharedIncident: null,
  following: new Set(), // userIds the signed-in caller follows, fetched once at sign-in like myVotes
  matches: [],
  currentMatchId: null,
  currentMatch: null,
  viewingUserId: null,
  livePollTimer: null,
  liveKnownIncidentIds: new Set(),
};

const loadFollowing = async () => {
  if (!auth.isSignedIn()) {
    state.following = new Set();
    return;
  }
  try {
    const {following} = await api("/api/me/following", {requireAuth: true});
    state.following = new Set(following);
  } catch {
    state.following = new Set();
  }
};

/* ---------------- Icons ---------------- */

const iconUp = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 19V5M5 12l7-7 7 7"/></svg>`;
const iconDown = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="M12 5v14M19 12l-7 7-7-7"/></svg>`;
const iconThread = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/></svg>`;
const iconShare = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="M8.6 13.5l6.8 4M15.4 6.5l-6.8 4"/></svg>`;
const iconEdit = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.85 2.83 0 114 4L7.5 20.5 2 22l1.5-5.5z"/></svg>`;
const iconTrash = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0l-1 14a2 2 0 01-2 2H7a2 2 0 01-2-2L4 6"/></svg>`;
const iconPlay = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M10 8l6 4-6 4V8z" fill="currentColor" stroke="none"/></svg>`;
const iconImage = `<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><circle cx="8.5" cy="8.5" r="1.5"/><path d="M21 15l-5-5L5 21"/></svg>`;

/* ---------------- Toast ---------------- */

let toastTimer;
const showToast = (message) => {
  let el = document.getElementById("toast");
  if (!el) {
    el = document.createElement("div");
    el.id = "toast";
    el.className = "toast";
    document.body.appendChild(el);
  }
  el.textContent = message;
  el.classList.add("show");
  clearTimeout(toastTimer);
  toastTimer = setTimeout(() => el.classList.remove("show"), 3200);
};

/* ---------------- Rendering ---------------- */

const statusLabel = (s) => ({disputed: "Disputed", under_review: "Under Review", upheld: "Call Upheld"}[s] || s);

const renderSportSwitcher = () => {
  const el = document.getElementById("sportSwitcher");
  el.innerHTML = state.sports
    .map(
      (s) => `<button class="sport-chip ${s.sportId === state.currentSport ? "active" : ""}" data-sport="${s.sportId}">${s.icon || ""} ${s.name}</button>`,
    )
    .join("");
  el.querySelectorAll("[data-sport]").forEach((btn) => {
    btn.addEventListener("click", () => selectSport(btn.dataset.sport));
  });
};

const renderRules = () => {
  const sport = state.sports.find((s) => s.sportId === state.currentSport);
  document.getElementById("rulesTitle").textContent = sport?.ruleBookLabel || "Rule Book";
  const list = document.getElementById("rulesList");
  if (!state.rules.length) {
    list.innerHTML = `<div class="empty-state">No rules published for this sport yet.</div>`;
    document.getElementById("ruleDetail").innerHTML = "";
    return;
  }
  list.innerHTML = state.rules
    .map(
      (r, i) => `
      <button class="rule-item ${i === state.activeRuleIndex ? "active" : ""}" data-rule-index="${i}">
        <span class="rule-num">${r.ruleId.toUpperCase()}</span>
        <span class="rule-name">${r.name}</span>
      </button>`,
    )
    .join("");
  list.querySelectorAll("[data-rule-index]").forEach((btn) => {
    btn.addEventListener("click", () => {
      state.activeRuleIndex = Number(btn.dataset.ruleIndex);
      renderRules();
    });
  });
  const d = state.rules[state.activeRuleIndex] || state.rules[0];
  document.getElementById("ruleDetail").innerHTML = `
    <div class="rd-num">${d.ruleId.toUpperCase()}</div>
    <h3>${d.name}</h3>
    <p>${d.body}</p>
    ${d.tag ? `<span class="rd-tag">${d.tag}</span>` : ""}
  `;
};

const isMineOrAdmin = (authorId) =>
  Boolean(auth.session && (auth.session.userId === authorId || auth.isAdmin()));

const renderCommentTools = (c, incidentId) => `
  <div class="comment-tools">
    <button class="reply-toggle" data-reply-for="${c.commentId}">Reply</button>
    ${
      isMineOrAdmin(c.authorId)
        ? `<button data-edit-comment="${c.commentId}" data-incident="${incidentId}">Edit</button>
           <button data-delete-comment="${c.commentId}" data-incident="${incidentId}">Delete</button>`
        : ""
    }
  </div>`;

const renderComments = (incidentId) => {
  const comments = state.commentsByIncident.get(incidentId) || [];
  const top = comments.filter((c) => !c.parentCommentId);
  const repliesOf = (id) => comments.filter((c) => c.parentCommentId === id);
  return top
    .map(
      (c) => `
      <div class="comment">
        <div class="avatar">${(c.authorDisplayName || "F").slice(0, 2).toUpperCase()}</div>
        <div class="comment-body">
          <div class="comment-head"><button class="comment-user author-link" data-author="${c.authorId}" data-author-name="${escapeHtml(c.authorDisplayName)}">${escapeHtml(c.authorDisplayName)}</button><span class="comment-time">${new Date(c.createdAt).toLocaleString()}</span></div>
          <div class="comment-text" data-comment-text="${c.commentId}">${escapeHtml(c.text)}</div>
          ${renderCommentTools(c, incidentId)}
          <div class="reply-box" id="reply-${c.commentId}">
            <input type="text" placeholder="Branch this thread..." id="replyInput-${c.commentId}">
            <button data-reply-submit="${c.commentId}" data-incident="${incidentId}">Reply</button>
          </div>
          ${repliesOf(c.commentId)
            .map(
              (r) => `
            <div class="comment reply">
              <div class="avatar">${(r.authorDisplayName || "F").slice(0, 2).toUpperCase()}</div>
              <div class="comment-body">
                <div class="comment-head"><button class="comment-user author-link" data-author="${r.authorId}" data-author-name="${escapeHtml(r.authorDisplayName)}">${escapeHtml(r.authorDisplayName)}</button><span class="comment-time">${new Date(r.createdAt).toLocaleString()}</span></div>
                <div class="comment-text" data-comment-text="${r.commentId}">${escapeHtml(r.text)}</div>
                ${renderCommentTools(r, incidentId)}
              </div>
            </div>`,
            )
            .join("")}
        </div>
      </div>`,
    )
    .join("");
};

const escapeHtml = (value) =>
  String(value).replace(/[&<>"']/g, (c) => ({"&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;"}[c]));

const renderTicketHtml = (t) => {
  const myVote = state.myVotes.get(t.incidentId) || 0;
  const threadOpen = state.openThreads.has(t.incidentId);
  const isOwner = isMineOrAdmin(t.authorId);
  return `
      <div>
        <div class="ticket">
          <div class="ticket-stub">
            <div class="stub-clock">
              <div class="min">${escapeHtml(t.clockLabel)}</div>
              <div class="lbl">Match Clock</div>
            </div>
            ${t.ruleLabel ? `<div class="stub-law"><b>${escapeHtml(t.ruleId || "")}</b>${escapeHtml(t.ruleLabel)}</div>` : ""}
          </div>
          <div class="ticket-body">
            <div class="ticket-top">
              <div>
                <div class="ticket-match">
                  ${escapeHtml(t.matchLabel)}
                  ${t.authorType === "official" ? '<span class="official-badge">RefRating</span>' : `&middot; by <button class="author-link" data-author="${t.authorId}" data-author-name="${escapeHtml(t.authorDisplayName)}">${escapeHtml(t.authorDisplayName)}</button>`}
                </div>
                <div class="ticket-title">${escapeHtml(t.title)}</div>
              </div>
              <span class="status-pill status-${t.disputeStatus}">${statusLabel(t.disputeStatus)}</span>
            </div>
            ${t.infringedTeam ? `<p class="ticket-infringed">Infringed team: <b>${escapeHtml(t.infringedTeam)}</b></p>` : ""}
            <p class="ticket-desc">${escapeHtml(t.description)}</p>
            ${t.mediaUrl ? `<a class="ticket-media-link" href="${escapeHtml(t.mediaUrl)}" target="_blank" rel="noopener">${iconPlay} Watch the evidence clip</a>` : ""}
            <div class="ticket-foot">
              <div class="vote-block">
                <button class="vote-btn ${myVote === 1 ? "voted-up" : ""}" data-vote="1" data-incident="${t.incidentId}">${iconUp}</button>
                <span class="vote-tally">${t.voteScore}</span>
                <button class="vote-btn ${myVote === -1 ? "voted-down" : ""}" data-vote="-1" data-incident="${t.incidentId}">${iconDown}</button>
              </div>
              <div class="ticket-actions">
                <button class="link-btn" data-toggle-thread="${t.incidentId}">${iconThread} ${t.commentCount} comments</button>
                <button class="link-btn" data-share="${t.incidentId}">${iconShare} Share</button>
                <button class="link-btn" data-verdict-card="${t.incidentId}">${iconImage} Card</button>
                ${
                  isOwner
                    ? `<button class="link-btn" data-edit-incident="${t.incidentId}">${iconEdit} Edit</button>
                       <button class="link-btn" data-delete-incident="${t.incidentId}">${iconTrash} Delete</button>`
                    : ""
                }
              </div>
            </div>
          </div>
        </div>
        <div class="thread ${threadOpen ? "open" : ""}" id="thread-${t.incidentId}">
          <div class="thread-inner">
            <div id="commentsFor-${t.incidentId}">${renderComments(t.incidentId)}</div>
            <div class="add-comment">
              <input type="text" placeholder="Add to the discussion..." id="commentInput-${t.incidentId}">
              <button data-comment-submit="${t.incidentId}">Post</button>
            </div>
          </div>
        </div>
      </div>`;
};

const renderTickets = () => {
  const grid = document.getElementById("ticketGrid");
  const filtered = state.incidents.filter(
    (t) => state.disputeFilter === "all" || t.disputeStatus === state.disputeFilter,
  );
  if (!filtered.length) {
    const scope = state.currentMatchId ? "this match" : "this sport";
    grid.innerHTML = `<div class="empty-state">No incidents logged yet for ${scope} &mdash; be the first to file one.</div>`;
    return;
  }
  grid.innerHTML = filtered.map(renderTicketHtml).join("");
  wireTicketEvents();
};

const renderSharedIncident = () => {
  const section = document.getElementById("sharedIncidentSection");
  if (!state.sharedIncident) {
    section.style.display = "none";
    section.innerHTML = "";
    return;
  }
  section.style.display = "block";
  section.innerHTML = `
    <div class="section-head">
      <div><h2>Shared Review</h2><div class="sub">DIRECT LINK</div></div>
    </div>
    <div class="ticket-grid">${renderTicketHtml(state.sharedIncident)}</div>
  `;
  wireTicketEvents();
};

const wireTicketEvents = () => {
  document.querySelectorAll("[data-vote]").forEach((btn) => {
    btn.addEventListener("click", () => onVote(btn.dataset.incident, Number(btn.dataset.vote)));
  });
  document.querySelectorAll("[data-toggle-thread]").forEach((btn) => {
    btn.addEventListener("click", () => onToggleThread(btn.dataset.toggleThread));
  });
  document.querySelectorAll("[data-comment-submit]").forEach((btn) => {
    btn.addEventListener("click", () => onSubmitComment(btn.dataset.commentSubmit));
  });
  document.querySelectorAll("[data-reply-for]").forEach((btn) => {
    btn.addEventListener("click", () => {
      document.getElementById(`reply-${btn.dataset.replyFor}`).classList.toggle("open");
    });
  });
  document.querySelectorAll("[data-reply-submit]").forEach((btn) => {
    btn.addEventListener("click", () =>
      onSubmitComment(btn.dataset.incident, btn.dataset.replySubmit),
    );
  });
  document.querySelectorAll("[data-share]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      openShareMenu(btn.dataset.share, btn);
    });
  });
  document.querySelectorAll("[data-edit-incident]").forEach((btn) => {
    btn.addEventListener("click", () => openEditIncidentModal(btn.dataset.editIncident));
  });
  document.querySelectorAll("[data-delete-incident]").forEach((btn) => {
    btn.addEventListener("click", () => onDeleteIncident(btn.dataset.deleteIncident));
  });
  document.querySelectorAll("[data-edit-comment]").forEach((btn) => {
    btn.addEventListener("click", () => onEditCommentStart(btn.dataset.editComment, btn.dataset.incident));
  });
  document.querySelectorAll("[data-delete-comment]").forEach((btn) => {
    btn.addEventListener("click", () => onDeleteComment(btn.dataset.deleteComment, btn.dataset.incident));
  });
  document.querySelectorAll("[data-author]").forEach((btn) => {
    btn.addEventListener("click", () => openAuthorProfile(btn.dataset.author, btn.dataset.authorName));
  });
  document.querySelectorAll("[data-verdict-card]").forEach((btn) => {
    btn.addEventListener("click", () => downloadVerdictCard(btn.dataset.verdictCard));
  });
};

/* ---------------- Actions ---------------- */

const loadSports = async () => {
  const {sports} = await api("/api/sports");
  state.sports = sports;
  if (!state.currentSport) {
    state.currentSport = sports.find((s) => s.sportId === "rugby")?.sportId || sports[0]?.sportId;
  }
};

const selectSport = async (sportId) => {
  state.currentSport = sportId;
  state.cursor = undefined;
  state.currentMatchId = null;
  state.currentMatch = null;
  state.sort = "recency";
  document.getElementById("logSub").textContent = "SORTED BY MOST DISPUTED · UPDATED LIVE";
  document.getElementById("velocitySortChip").style.display = "none";
  stopLivePolling();
  renderSportSwitcher();
  renderMatchSummaryBar();
  await Promise.all([loadRules(), loadIncidents(), loadMatches()]);
};

const refreshCurrentIncidentsView = async () => {
  if (state.currentMatchId) {
    await selectMatch(state.currentMatchId);
  } else {
    await loadIncidents();
  }
};

const loadRules = async () => {
  if (!state.currentSport) return;
  const {rules} = await api(`/api/sports/${state.currentSport}/rules`);
  state.rules = rules;
  state.activeRuleIndex = 0;
  renderRules();
};

const loadIncidents = async ({append = false} = {}) => {
  if (!state.currentSport) return;
  const params = new URLSearchParams({sportId: state.currentSport, sort: state.sort});
  if (state.cursor && append) params.set("cursor", state.cursor);
  const {incidents, cursor} = await api(`/api/incidents?${params.toString()}`);
  state.incidents = append ? [...state.incidents, ...incidents] : incidents;
  state.cursor = cursor;
  document.getElementById("loadMoreBtn").style.display = cursor ? "inline-block" : "none";
  renderTickets();
};

const onVote = async (incidentId, direction) => {
  if (!auth.isSignedIn()) return openAuthModal();
  try {
    const {myVote} = await api(`/api/incidents/${incidentId}/vote`, {method: "POST", body: {direction}, requireAuth: true});
    const prev = state.myVotes.get(incidentId) || 0;
    const incident = state.incidents.find((i) => i.incidentId === incidentId);
    if (incident) {
      if (prev === 1) incident.upvotes -= 1;
      if (prev === -1) incident.downvotes -= 1;
      if (myVote === 1) incident.upvotes += 1;
      if (myVote === -1) incident.downvotes += 1;
      incident.voteScore = incident.upvotes - incident.downvotes;
    }
    state.myVotes.set(incidentId, myVote);
    renderTickets();
  } catch (error) {
    showToast(error.message);
  }
};

const onToggleThread = async (incidentId) => {
  if (state.openThreads.has(incidentId)) {
    state.openThreads.delete(incidentId);
  } else {
    state.openThreads.add(incidentId);
    if (!state.commentsByIncident.has(incidentId)) {
      const {comments} = await api(`/api/incidents/${incidentId}/comments`);
      state.commentsByIncident.set(incidentId, comments);
    }
  }
  renderTickets();
};

const onSubmitComment = async (incidentId, parentCommentId) => {
  if (!auth.isSignedIn()) return openAuthModal();
  const inputId = parentCommentId ? `replyInput-${parentCommentId}` : `commentInput-${incidentId}`;
  const input = document.getElementById(inputId);
  const text = input.value.trim();
  if (!text) return;
  try {
    await api(`/api/incidents/${incidentId}/comments`, {
      method: "POST",
      body: {text, parentCommentId},
      requireAuth: true,
    });
    input.value = "";
    showToast("Comment submitted — awaiting moderation before it appears.");
  } catch (error) {
    showToast(error.message);
  }
};

/* ---------------- Comment edit/delete ---------------- */

const onEditCommentStart = (commentId, incidentId) => {
  const el = document.querySelector(`[data-comment-text="${commentId}"]`);
  const comments = state.commentsByIncident.get(incidentId) || [];
  const comment = comments.find((c) => c.commentId === commentId);
  if (!el || !comment) return;
  el.innerHTML = `
    <input type="text" value="${escapeHtml(comment.text)}" id="editCommentInput-${commentId}">
    <button data-save-comment="${commentId}" data-incident="${incidentId}">Save</button>
  `;
  document
    .querySelector(`[data-save-comment="${commentId}"]`)
    .addEventListener("click", () => onSaveComment(commentId, incidentId));
};

const onSaveComment = async (commentId, incidentId) => {
  const input = document.getElementById(`editCommentInput-${commentId}`);
  const text = input.value.trim();
  if (!text) return;
  try {
    const {reModerated} = await api(`/api/comments/${commentId}`, {
      method: "PATCH",
      body: {text},
      requireAuth: true,
    });
    const comments = state.commentsByIncident.get(incidentId) || [];
    if (reModerated) {
      state.commentsByIncident.set(incidentId, comments.filter((c) => c.commentId !== commentId));
      const incident = state.incidents.find((i) => i.incidentId === incidentId);
      if (incident) incident.commentCount = Math.max(0, incident.commentCount - 1);
      showToast("Comment updated — sent back for moderation.");
    } else {
      state.commentsByIncident.set(
        incidentId,
        comments.map((c) => (c.commentId === commentId ? {...c, text} : c)),
      );
      showToast("Comment updated.");
    }
    renderTickets();
    renderSharedIncident();
  } catch (error) {
    showToast(error.message);
  }
};

const onDeleteComment = async (commentId, incidentId) => {
  if (!confirm("Delete this comment?")) return;
  try {
    await api(`/api/comments/${commentId}`, {method: "DELETE", requireAuth: true});
    const comments = (state.commentsByIncident.get(incidentId) || []).filter((c) => c.commentId !== commentId);
    state.commentsByIncident.set(incidentId, comments);
    const incident = state.incidents.find((i) => i.incidentId === incidentId);
    if (incident) incident.commentCount = Math.max(0, incident.commentCount - 1);
    renderTickets();
    renderSharedIncident();
    showToast("Comment deleted.");
  } catch (error) {
    showToast(error.message);
  }
};

/* ---------------- Incident edit/delete ---------------- */

const setMatchFieldsEditable = (editable) => {
  ["f-sport", "f-team-a", "f-team-b", "f-match-date", "f-infringed-team"].forEach((id) => {
    document.getElementById(id).disabled = !editable;
  });
};

const updateInfringedTeamOptions = () => {
  const teamA = document.getElementById("f-team-a").value.trim();
  const teamB = document.getElementById("f-team-b").value.trim();
  const select = document.getElementById("f-infringed-team");
  const previous = select.value;
  if (!teamA || !teamB) {
    select.innerHTML = `<option value="">Enter both teams above first</option>`;
    return;
  }
  select.innerHTML =
    `<option value="">Choose the infringed team</option>` +
    [teamA, teamB].map((name) => `<option value="${escapeHtml(name)}">${escapeHtml(name)}</option>`).join("");
  if ([teamA, teamB].includes(previous)) select.value = previous;
};

/* ---------------- Team autocomplete (Highlightly) ----------------
   Suggests real team names while filing a review and, once both fields
   resolve to a known team, auto-fills the match date - prevents typo
   duplicates at the source instead of only merging them after the fact. */
const teamSearchState = {teamAIds: new Map(), teamBIds: new Map(), debounceTimers: {}};

const debounce = (key, fn, delay) => {
  clearTimeout(teamSearchState.debounceTimers[key]);
  teamSearchState.debounceTimers[key] = setTimeout(fn, delay);
};

const wireTeamAutocomplete = (inputId, datalistId, idMap) => {
  const input = document.getElementById(inputId);
  const datalist = document.getElementById(datalistId);
  input.addEventListener("input", () => {
    const q = input.value.trim();
    if (q.length < 2) return;
    debounce(inputId, async () => {
      try {
        const {teams} = await api(`/api/integrations/rugby-teams?q=${encodeURIComponent(q)}`);
        idMap.clear();
        datalist.innerHTML = teams
          .map((t) => {
            idMap.set(t.name.toLowerCase(), t.id);
            return `<option value="${escapeHtml(t.name)}"></option>`;
          })
          .join("");
      } catch {
        // Autocomplete is a convenience, not a required part of filing a review - fail silently.
      }
    }, 300);
  });
};

const maybeAutofillMatchDate = async () => {
  const teamA = document.getElementById("f-team-a").value.trim();
  const teamB = document.getElementById("f-team-b").value.trim();
  const dateField = document.getElementById("f-match-date");
  if (dateField.value.trim() || !teamA || !teamB) return;
  const teamAId = teamSearchState.teamAIds.get(teamA.toLowerCase());
  const teamBId = teamSearchState.teamBIds.get(teamB.toLowerCase());
  if (!teamAId || !teamBId) return;
  try {
    const {match} = await api(`/api/integrations/rugby-match-date?teamAId=${teamAId}&teamBId=${teamBId}`);
    if (match && !dateField.value.trim()) dateField.value = match.date;
  } catch {
    // Best-effort convenience only.
  }
};

const findKnownIncident = (incidentId) =>
  state.incidents.find((i) => i.incidentId === incidentId) ||
  (state.sharedIncident?.incidentId === incidentId ? state.sharedIncident : undefined);

const openEditIncidentModal = async (incidentId) => {
  const incident = findKnownIncident(incidentId);
  if (!incident) return;
  document.getElementById("reviewModalTitle").textContent = "Edit Review";
  document.getElementById("f-dispute-field").style.display = "none";
  document.getElementById("reviewFormHint").textContent =
    incident.authorType === "fan" && incident.moderationStatus === "published"
      ? "Editing a published review sends it back for moderation."
      : "Save your changes.";
  populateReviewForm();
  document.getElementById("f-sport").value = incident.sportId;
  await populateReviewRules();
  const [teamA, teamB] = (incident.matchLabel || "").split(" vs ").map((s) => s.trim());
  document.getElementById("f-team-a").value = teamA || "";
  document.getElementById("f-team-b").value = teamB || "";
  updateInfringedTeamOptions();
  document.getElementById("f-infringed-team").value = incident.infringedTeam || "";
  document.getElementById("f-clock").value = incident.clockLabel;
  document.getElementById("f-rule").value = incident.ruleId || "";
  document.getElementById("f-title").value = incident.title;
  document.getElementById("f-desc").value = incident.description;
  document.getElementById("f-media-url").value = incident.mediaUrl || "";
  setMatchFieldsEditable(false);
  document.getElementById("reviewSubmitBtn").onclick = () => submitEditIncident(incidentId);
  openModal("reviewModalOverlay");
};

const submitEditIncident = async (incidentId) => {
  const title = document.getElementById("f-title").value.trim();
  const description = document.getElementById("f-desc").value.trim();
  const clockLabel = document.getElementById("f-clock").value.trim() || "00:00";
  const ruleSelect = document.getElementById("f-rule");
  const ruleId = ruleSelect.value || undefined;
  const ruleLabel = ruleId ? ruleSelect.selectedOptions[0].dataset.label : undefined;
  const mediaUrl = document.getElementById("f-media-url").value.trim() || undefined;
  if (!title || !description) {
    showToast("Headline and description are required.");
    return;
  }
  try {
    const {reModerated} = await api(`/api/incidents/${incidentId}`, {
      method: "PATCH",
      body: {title, description, clockLabel, ruleId, ruleLabel, mediaUrl},
      requireAuth: true,
    });
    showToast(reModerated ? "Updated — sent back for moderation." : "Updated.");
    closeModal("reviewModalOverlay");
    setMatchFieldsEditable(true);
    if (reModerated) {
      state.incidents = state.incidents.filter((i) => i.incidentId !== incidentId);
      if (state.sharedIncident?.incidentId === incidentId) state.sharedIncident = null;
    } else {
      const patch = {
        title,
        description,
        clockLabel,
        ruleId: ruleId ?? null,
        ruleLabel: ruleLabel ?? null,
        mediaUrl: mediaUrl ?? null,
      };
      state.incidents = state.incidents.map((i) => (i.incidentId === incidentId ? {...i, ...patch} : i));
      if (state.sharedIncident?.incidentId === incidentId) {
        state.sharedIncident = {...state.sharedIncident, ...patch};
      }
    }
    renderTickets();
    renderSharedIncident();
  } catch (error) {
    showToast(error.message);
  }
};

const onDeleteIncident = async (incidentId) => {
  if (!confirm("Delete this review? This can't be undone.")) return;
  try {
    await api(`/api/incidents/${incidentId}`, {method: "DELETE", requireAuth: true});
    state.incidents = state.incidents.filter((i) => i.incidentId !== incidentId);
    if (state.sharedIncident?.incidentId === incidentId) state.sharedIncident = null;
    renderTickets();
    renderSharedIncident();
    showToast("Review deleted.");
  } catch (error) {
    showToast(error.message);
  }
};

/* ---------------- Share ---------------- */

const buildShareUrl = (incidentId) => `${location.origin}${location.pathname}?incident=${incidentId}`;

const openShareMenu = (incidentId, anchorEl) => {
  const incident = findKnownIncident(incidentId);
  const url = buildShareUrl(incidentId);
  const text = incident ? `${incident.title} — RefRating` : "RefRating";

  let menu = document.getElementById("shareMenu");
  if (!menu) {
    menu = document.createElement("div");
    menu.id = "shareMenu";
    menu.className = "share-menu";
    document.body.appendChild(menu);
  }
  menu.innerHTML = `
    ${navigator.share ? `<button data-share-native>Share…</button>` : ""}
    <button data-share-copy>Copy link</button>
    <a href="https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}" target="_blank" rel="noopener">Share to X</a>
    <a href="https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}" target="_blank" rel="noopener">Share to Facebook</a>
    <a href="https://wa.me/?text=${encodeURIComponent(`${text} ${url}`)}" target="_blank" rel="noopener">Share to WhatsApp</a>
  `;
  const rect = anchorEl.getBoundingClientRect();
  menu.style.top = `${rect.bottom + window.scrollY + 6}px`;
  menu.style.left = `${rect.left + window.scrollX}px`;
  menu.classList.add("open");

  menu.querySelector("[data-share-native]")?.addEventListener("click", async () => {
    try {
      await navigator.share({title: text, url});
    } catch {
      // user cancelled the native share sheet - not an error
    }
    menu.classList.remove("open");
  });
  menu.querySelector("[data-share-copy]").addEventListener("click", async () => {
    await navigator.clipboard.writeText(url);
    showToast("Link copied.");
    menu.classList.remove("open");
  });
};

document.addEventListener("click", (e) => {
  const menu = document.getElementById("shareMenu");
  if (menu && menu.classList.contains("open") && !menu.contains(e.target) && !e.target.closest("[data-share]")) {
    menu.classList.remove("open");
  }
});

/* ---------------- Author profile & follow ---------------- */

const openAuthorProfile = async (userId, fallbackName) => {
  state.viewingUserId = userId;
  document.getElementById("authorProfileName").textContent = fallbackName || "Profile";
  document.getElementById("authorProfileBadge").innerHTML = "";
  document.getElementById("authorProfileBio").textContent = "";
  document.getElementById("authorProfileMeta").textContent = "Loading…";
  document.getElementById("authorProfileStats").innerHTML = "";
  document.getElementById("authorFollowBtn").style.display = "none";
  openModal("authorProfileModalOverlay");
  try {
    const {profile} = await api(`/api/users/${userId}`);
    document.getElementById("authorProfileName").textContent = profile.displayName;
    document.getElementById("authorProfileBadge").innerHTML = profile.isOfficial
      ? '<span class="official-badge">RefRating</span>'
      : "";
    document.getElementById("authorProfileBio").textContent = profile.bio || "No bio yet.";
    document.getElementById("authorProfileMeta").textContent =
      `Member since ${new Date(profile.memberSince).toLocaleDateString()}`;
    document.getElementById("authorProfileStats").innerHTML = `
      <div class="stat-mini"><div class="num">${profile.followerCount}</div><div class="lbl">Followers</div></div>
      <div class="stat-mini"><div class="num">${profile.followingCount}</div><div class="lbl">Following</div></div>
    `;
    renderFollowButton(userId);
  } catch (error) {
    document.getElementById("authorProfileMeta").textContent = error.message;
  }
};

const renderFollowButton = (userId) => {
  const btn = document.getElementById("authorFollowBtn");
  if (!auth.isSignedIn() || userId === auth.session.userId) {
    btn.style.display = "none";
    return;
  }
  btn.style.display = "block";
  const isFollowing = state.following.has(userId);
  btn.textContent = isFollowing ? "Following" : "Follow";
  btn.className = isFollowing ? "btn-following" : "modal-submit";
  btn.onclick = () => toggleFollow(userId);
};

const toggleFollow = async (userId) => {
  const isFollowing = state.following.has(userId);
  try {
    if (isFollowing) {
      await api(`/api/users/${userId}/follow`, {method: "DELETE", requireAuth: true});
      state.following.delete(userId);
    } else {
      await api(`/api/users/${userId}/follow`, {method: "POST", requireAuth: true});
      state.following.add(userId);
    }
    await openAuthorProfile(userId, document.getElementById("authorProfileName").textContent);
  } catch (error) {
    showToast(error.message);
  }
};

/* ---------------- Verdict Card (client-side share image, no backend) ---------------- */

const downloadVerdictCard = async (incidentId) => {
  const t = findKnownIncident(incidentId);
  if (!t) return;
  const canvas = document.createElement("canvas");
  canvas.width = 1080;
  canvas.height = 1080;
  const ctx = canvas.getContext("2d");

  ctx.fillStyle = "#0B2E1F";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#FFD23F";
  ctx.font = "700 34px 'JetBrains Mono', monospace";
  ctx.fillText("REFRATING", 60, 90);
  ctx.fillStyle = "#8A9A8E";
  ctx.font = "600 24px 'JetBrains Mono', monospace";
  ctx.fillText((t.matchLabel || "").toUpperCase(), 60, 130);

  ctx.fillStyle = "#FFD23F";
  ctx.font = "700 90px 'JetBrains Mono', monospace";
  ctx.fillText(t.clockLabel || "", 60, 260);
  ctx.fillStyle = "#8A9A8E";
  ctx.font = "600 20px 'JetBrains Mono', monospace";
  ctx.fillText("MATCH CLOCK", 60, 295);

  const wrapText = (text, x, y, maxWidth, lineHeight) => {
    const words = String(text).split(" ");
    let line = "";
    let cursorY = y;
    for (const word of words) {
      const test = line ? `${line} ${word}` : word;
      if (ctx.measureText(test).width > maxWidth && line) {
        ctx.fillText(line, x, cursorY);
        line = word;
        cursorY += lineHeight;
      } else {
        line = test;
      }
    }
    if (line) ctx.fillText(line, x, cursorY);
    return cursorY;
  };

  ctx.fillStyle = "#F5F3EC";
  ctx.font = "800 46px 'Archivo', sans-serif";
  const titleBottom = wrapText(t.title || "", 60, 400, 960, 56);

  ctx.fillStyle = "rgba(245,243,236,0.82)";
  ctx.font = "400 28px 'Archivo', sans-serif";
  wrapText(t.description || "", 60, titleBottom + 70, 960, 40);

  ctx.fillStyle = "rgba(255,255,255,0.15)";
  ctx.fillRect(60, 860, 960, 1);

  ctx.fillStyle = "#FFD23F";
  ctx.font = "700 56px 'JetBrains Mono', monospace";
  ctx.fillText(`${t.voteScore >= 0 ? "+" : ""}${t.voteScore}`, 60, 950);
  ctx.fillStyle = "#8A9A8E";
  ctx.font = "600 20px 'JetBrains Mono', monospace";
  ctx.fillText("VOTE SCORE", 60, 985);

  ctx.fillStyle = "#8A9A8E";
  ctx.font = "600 22px 'JetBrains Mono', monospace";
  ctx.textAlign = "right";
  ctx.fillText("refrating.online", 1020, 985);
  ctx.textAlign = "left";

  canvas.toBlob(async (blob) => {
    if (!blob) return;
    const file = new File([blob], `refrating-${incidentId}.png`, {type: "image/png"});
    if (navigator.canShare && navigator.canShare({files: [file]})) {
      try {
        await navigator.share({files: [file], title: t.title, text: `${t.title} — RefRating`});
        return;
      } catch {
        // fall through to download if the user cancels or share fails
      }
    }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `refrating-${incidentId}.png`;
    a.click();
    URL.revokeObjectURL(url);
    showToast("Verdict card downloaded.");
  }, "image/png");
};

/* ---------------- Matches ---------------- */

const renderMatches = () => {
  const row = document.getElementById("matchRow");
  if (!state.matches.length) {
    row.innerHTML = `<div class="empty-state">No matches logged yet for this sport &mdash; filing a review creates one.</div>`;
    return;
  }
  row.innerHTML = state.matches
    .map(
      (m) => `
      <div class="match-card ${m.matchId === state.currentMatchId ? "active" : ""}" data-match="${m.matchId}">
        ${m.isLive ? `<div class="live-badge"><span class="live-dot"></span>LIVE</div>` : ""}
        <div class="match-teams">${escapeHtml(m.teamA)} vs ${escapeHtml(m.teamB)}</div>
        <div class="match-date">${m.matchDate ? escapeHtml(m.matchDate) : "Date not set"}</div>
        ${m.refereeName ? `<div class="match-referee">Ref: <button class="referee-link" data-referee="${escapeHtml(m.refereeName)}">${escapeHtml(m.refereeName)}</button></div>` : ""}
        <div class="match-stats">
          <span class="match-chip">${m.incidentCount} incident${m.incidentCount === 1 ? "" : "s"}</span>
          ${m.disputedCount ? `<span class="match-chip disputed">${m.disputedCount} disputed</span>` : ""}
          ${m.upheldCount ? `<span class="match-chip upheld">${m.upheldCount} upheld</span>` : ""}
        </div>
      </div>`,
    )
    .join("");
  row.querySelectorAll("[data-match]").forEach((card) => {
    card.addEventListener("click", () => selectMatch(card.dataset.match));
  });
  row.querySelectorAll("[data-referee]").forEach((btn) => {
    btn.addEventListener("click", (e) => {
      e.stopPropagation();
      openRefereeModal(btn.dataset.referee);
    });
  });
};

const loadMatches = async () => {
  if (!state.currentSport) return;
  const {matches} = await api(`/api/sports/${state.currentSport}/matches`);
  state.matches = matches;
  renderMatches();
};

const renderMatchSummaryBar = () => {
  const bar = document.getElementById("matchSummaryBar");
  const clearBtn = document.getElementById("clearMatchFilterBtn");
  if (!state.currentMatch) {
    bar.style.display = "none";
    clearBtn.style.display = "none";
    return;
  }
  const m = state.currentMatch;
  clearBtn.style.display = "inline-block";
  bar.style.display = "flex";
  bar.innerHTML = `
    <div>
      <h3>${m.isLive ? `<span class="live-badge"><span class="live-dot"></span>LIVE</span> ` : ""}${escapeHtml(m.teamA)} vs ${escapeHtml(m.teamB)}</h3>
      <div class="sub">${m.matchDate ? escapeHtml(m.matchDate) : "Date not set"} &middot; MATCH SUMMARY</div>
      ${m.refereeName ? `<div class="match-referee">Ref: <button class="referee-link" id="summaryRefereeLink">${escapeHtml(m.refereeName)}</button></div>` : ""}
    </div>
    <div class="match-stats">
      <span class="match-chip">${m.incidentCount} total</span>
      <span class="match-chip disputed">${m.disputedCount} disputed</span>
      <span class="match-chip">${m.underReviewCount} under review</span>
      <span class="match-chip upheld">${m.upheldCount} upheld</span>
      ${auth.isAdmin() ? `<button class="btn-ghost" id="toggleLiveBtn">${m.isLive ? "End live mode" : "Go live"}</button>` : ""}
    </div>
  `;
  const toggleBtn = document.getElementById("toggleLiveBtn");
  if (toggleBtn) toggleBtn.addEventListener("click", () => toggleMatchLive(m.matchId, !m.isLive));
  const refLink = document.getElementById("summaryRefereeLink");
  if (refLink) refLink.addEventListener("click", () => openRefereeModal(m.refereeName));
};

const openRefereeModal = async (refereeName) => {
  document.getElementById("refereeModalName").textContent = refereeName;
  const list = document.getElementById("refereeMatchList");
  list.innerHTML = `<div class="empty-state">Loading matches&hellip;</div>`;
  openModal("refereeModalOverlay");
  try {
    const {matches} = await api(`/api/referees/matches?name=${encodeURIComponent(refereeName)}`);
    if (!matches.length) {
      list.innerHTML = `<div class="empty-state">No matches found for this official yet.</div>`;
      return;
    }
    list.innerHTML = matches
      .map(
        (m) => `
        <div class="match-card referee-match-row" data-match="${m.matchId}">
          <div class="match-teams">${escapeHtml(m.teamA)} vs ${escapeHtml(m.teamB)}</div>
          <div class="match-date">${m.matchDate ? escapeHtml(m.matchDate) : "Date not set"}</div>
          <div class="match-stats">
            <span class="match-chip">${m.incidentCount} incident${m.incidentCount === 1 ? "" : "s"}</span>
            ${m.disputedCount ? `<span class="match-chip disputed">${m.disputedCount} disputed</span>` : ""}
            ${m.upheldCount ? `<span class="match-chip upheld">${m.upheldCount} upheld</span>` : ""}
          </div>
        </div>`,
      )
      .join("");
    list.querySelectorAll("[data-match]").forEach((card) => {
      card.addEventListener("click", () => {
        closeModal("refereeModalOverlay");
        selectMatch(card.dataset.match);
      });
    });
  } catch {
    list.innerHTML = `<div class="empty-state">Couldn't load matches right now &mdash; try again shortly.</div>`;
  }
};

const toggleMatchLive = async (matchId, isLive) => {
  await api(`/api/admin/matches/${matchId}/live`, {method: "PATCH", body: {isLive}, requireAuth: true});
  showToast(isLive ? "Match is now live." : "Live mode ended.");
  await selectMatch(matchId);
  await loadMatches();
};

const syncSortChipsUI = () => {
  document.querySelectorAll(".chip[data-sort]").forEach((chip) => {
    chip.classList.toggle("active", chip.dataset.sort === state.sort);
  });
};

const stopLivePolling = () => {
  if (state.livePollTimer) clearInterval(state.livePollTimer);
  state.livePollTimer = null;
  document.getElementById("newIncidentsBanner").style.display = "none";
};

const pollLiveMatch = async (matchId) => {
  try {
    const {incidents} = await api(`/api/incidents?matchId=${matchId}&sort=recency&limit=50`);
    const newOnes = incidents.filter((i) => !state.liveKnownIncidentIds.has(i.incidentId));
    if (newOnes.length > 0 && state.liveKnownIncidentIds.size > 0) {
      const banner = document.getElementById("newIncidentsBanner");
      banner.textContent = `${newOnes.length} new incident${newOnes.length === 1 ? "" : "s"} — tap to view`;
      banner.style.display = "block";
    }
  } catch {
    // Polling is a background convenience - a transient failure just waits for the next tick.
  }
};

const startLivePolling = (matchId) => {
  stopLivePolling();
  state.liveKnownIncidentIds = new Set(state.incidents.map((i) => i.incidentId));
  state.livePollTimer = setInterval(() => pollLiveMatch(matchId), 18_000);
};

const selectMatch = async (matchId) => {
  state.currentMatchId = matchId;
  state.cursor = undefined;
  renderMatches();
  const {match} = await api(`/api/matches/${matchId}`);
  state.currentMatch = match;
  document.getElementById("velocitySortChip").style.display = match.isLive ? "inline-block" : "none";
  state.sort = match.isLive ? "velocity" : "recency";
  const {incidents, cursor} = await api(`/api/incidents?matchId=${matchId}&sort=${state.sort}`);
  state.incidents = incidents;
  state.cursor = cursor;
  document.getElementById("loadMoreBtn").style.display = cursor ? "inline-block" : "none";
  document.getElementById("logSub").textContent = "FILTERED TO ONE MATCH";
  syncSortChipsUI();
  renderMatchSummaryBar();
  renderTickets();
  document.getElementById("log").scrollIntoView({behavior: "smooth"});
  if (match.isLive) {
    startLivePolling(matchId);
  } else {
    stopLivePolling();
  }
};

const clearMatchFilter = async () => {
  state.currentMatchId = null;
  state.currentMatch = null;
  state.cursor = undefined;
  state.sort = "recency";
  document.getElementById("logSub").textContent = "SORTED BY MOST DISPUTED · UPDATED LIVE";
  document.getElementById("velocitySortChip").style.display = "none";
  stopLivePolling();
  syncSortChipsUI();
  renderMatches();
  renderMatchSummaryBar();
  await loadIncidents();
};

/* ---------------- Deep-linked shared incident ---------------- */

const showSharedIncident = async (incidentId) => {
  try {
    const {incident} = await api(`/api/incidents/${incidentId}`);
    state.sharedIncident = incident;
    renderSharedIncident();
    document.getElementById("sharedIncidentSection").scrollIntoView({behavior: "smooth"});
  } catch {
    showToast("Could not load that review — it may have been removed.");
  }
};

/* ---------------- File a review modal ---------------- */

const openModal = (id) => document.getElementById(id).classList.add("open");
const closeModal = (id) => document.getElementById(id).classList.remove("open");

const populateReviewForm = () => {
  const sportSelect = document.getElementById("f-sport");
  sportSelect.innerHTML = state.sports.map((s) => `<option value="${s.sportId}">${s.name}</option>`).join("");
  sportSelect.value = state.currentSport;
  populateReviewRules();
  sportSelect.onchange = populateReviewRules;
  document.getElementById("f-team-a").value = "";
  document.getElementById("f-team-b").value = "";
  document.getElementById("f-match-date").value = "";
  document.getElementById("f-media-url").value = "";
  updateInfringedTeamOptions();
};

const populateReviewRules = async () => {
  const sportId = document.getElementById("f-sport").value;
  const {rules} = await api(`/api/sports/${sportId}/rules`);
  const ruleSelect = document.getElementById("f-rule");
  ruleSelect.innerHTML =
    `<option value="">Other / unsure</option>` +
    rules.map((r) => `<option value="${r.ruleId}" data-label="${r.name}">${r.ruleId.toUpperCase()} — ${r.name}</option>`).join("");
};

const submitReview = async ({asAdmin = false} = {}) => {
  const sportId = document.getElementById("f-sport").value;
  const teamA = document.getElementById("f-team-a").value.trim();
  const teamB = document.getElementById("f-team-b").value.trim();
  const matchDate = document.getElementById("f-match-date").value.trim() || undefined;
  const infringedTeam = document.getElementById("f-infringed-team").value;
  const clockLabel = document.getElementById("f-clock").value.trim() || "00:00";
  const ruleSelect = document.getElementById("f-rule");
  const ruleId = ruleSelect.value || undefined;
  const ruleLabel = ruleId ? ruleSelect.selectedOptions[0].dataset.label : undefined;
  const title = document.getElementById("f-title").value.trim();
  const description = document.getElementById("f-desc").value.trim();
  const mediaUrl = document.getElementById("f-media-url").value.trim() || undefined;
  if (!teamA || !teamB || !title || !description) {
    showToast("Both teams, a headline, and a description are required.");
    return;
  }
  if (!asAdmin && !infringedTeam) {
    showToast("Pick which team was infringed.");
    return;
  }
  const body = {sportId, teamA, teamB, matchDate, clockLabel, ruleId, ruleLabel, title, description, mediaUrl};
  if (infringedTeam) body.infringedTeam = infringedTeam;
  if (asAdmin) {
    body.disputeStatus = document.getElementById("f-dispute-status").value;
  }
  try {
    if (asAdmin) {
      await api("/api/admin/incidents", {method: "POST", body, requireAuth: true});
      showToast("Official review published.");
      await refreshCurrentIncidentsView();
      await loadMatches();
    } else {
      if (!auth.isSignedIn()) return openAuthModal();
      await api("/api/incidents", {method: "POST", body, requireAuth: true});
      showToast("Submitted — it will appear once it clears moderation.");
    }
    closeModal("reviewModalOverlay");
    ["f-team-a", "f-team-b", "f-match-date", "f-clock", "f-title", "f-desc", "f-media-url"].forEach(
      (id) => (document.getElementById(id).value = ""),
    );
  } catch (error) {
    if (error.code === "DUPLICATE_LIKELY" && error.existingIncidentId) {
      closeModal("reviewModalOverlay");
      showToast(`Looks like it's already filed: "${error.existingTitle}" — jumping to it.`);
      await showSharedIncident(error.existingIncidentId);
    } else {
      showToast(error.message);
    }
  }
};

/* ---------------- Auth modal ---------------- */

let pendingConfirmEmail = null;
let pendingPostSignInAction = null;

const openAuthModal = (postSignInAction) => {
  pendingPostSignInAction = typeof postSignInAction === "function" ? postSignInAction : null;
  showAuthStep("signIn");
  openModal("authModalOverlay");
};

const showAuthStep = (step) => {
  document.getElementById("authStepSignIn").style.display = step === "signIn" ? "block" : "none";
  document.getElementById("authStepSignUp").style.display = step === "signUp" ? "block" : "none";
  document.getElementById("authStepConfirm").style.display = step === "confirm" ? "block" : "none";
  document.getElementById("authError").textContent = "";
};

const refreshAccountUi = () => {
  const el = document.getElementById("topbarActions");
  const adminLink = document.getElementById("adminNavLink");
  if (auth.isSignedIn()) {
    el.innerHTML = `<span class="mono" style="font-size:12px;color:var(--sage);">${auth.session.email}</span> <button class="btn-account" id="profileNavBtn">Profile</button> <button class="btn-signout" id="signOutBtn">Sign out</button>`;
    document.getElementById("signOutBtn").addEventListener("click", () => {
      auth.clear();
      state.following = new Set();
      refreshAccountUi();
      showToast("Signed out.");
    });
    document.getElementById("profileNavBtn").addEventListener("click", openProfileModal);
    adminLink.style.display = auth.isAdmin() ? "inline" : "none";
  } else {
    el.innerHTML = `<button class="btn-account" id="signInNavBtn">Sign in</button>`;
    document.getElementById("signInNavBtn").addEventListener("click", openAuthModal);
    adminLink.style.display = "none";
  }
};

/* ---------------- Profile ---------------- */

const openProfileModal = async () => {
  try {
    const {profile} = await api("/api/me", {requireAuth: true});
    document.getElementById("profile-username").value = profile.displayName || "";
    document.getElementById("profile-bio").value = profile.bio || "";
    document.getElementById("profileError").textContent = "";
    openModal("profileModalOverlay");
  } catch (error) {
    showToast(error.message);
  }
};

const saveProfile = async () => {
  const username = document.getElementById("profile-username").value.trim();
  const bio = document.getElementById("profile-bio").value.trim();
  document.getElementById("profileError").textContent = "";
  try {
    await api("/api/me", {method: "PATCH", body: {username, bio}, requireAuth: true});
    showToast("Profile updated.");
    closeModal("profileModalOverlay");
    renderTickets();
    renderSharedIncident();
  } catch (error) {
    document.getElementById("profileError").textContent = error.message;
  }
};

const deleteAccount = async () => {
  if (
    !confirm(
      "Delete your account? Your profile and username are removed permanently. Past reviews/comments stay under your old name. This can't be undone.",
    )
  ) {
    return;
  }
  try {
    // Delete our own profile row + release the username claim FIRST, while
    // the access token is still fully valid - Cognito's DeleteUser (below)
    // invalidates it as part of removing the account.
    await api("/api/me", {method: "DELETE", requireAuth: true});
    await auth.deleteCognitoAccount();
    auth.clear();
    closeModal("profileModalOverlay");
    refreshAccountUi();
    showToast("Account deleted.");
  } catch (error) {
    document.getElementById("profileError").textContent = error.message;
  }
};

/* ---------------- Admin ---------------- */

const showAdminSection = () => {
  document.getElementById("admin").style.display = "block";
  document.getElementById("admin").scrollIntoView({behavior: "smooth"});
  loadModerationQueue();
};

const loadModerationQueue = async () => {
  try {
    const {items} = await api("/api/admin/moderation-queue", {requireAuth: true});
    const container = document.getElementById("adminQueue");
    if (!items.length) {
      container.innerHTML = `<div class="empty-state">Queue is empty.</div>`;
      return;
    }
    container.innerHTML = items
      .map(
        (item) => `
      <div class="queue-row">
        <div>
          <div class="queue-meta">${item.contentType.toUpperCase()} · ${item.moderationStatus} · ${item.authorDisplayName}</div>
          <div>${escapeHtml(item.title || item.text || "")}</div>
        </div>
        <div class="queue-actions">
          <button class="btn-approve" data-approve="${item.contentType}:${item.incidentId || item.commentId}">Approve</button>
          <button class="btn-reject" data-reject="${item.contentType}:${item.incidentId || item.commentId}">Reject</button>
        </div>
      </div>`,
      )
      .join("");
    container.querySelectorAll("[data-approve]").forEach((btn) => {
      btn.addEventListener("click", () => onModerate(btn.dataset.approve, "approve"));
    });
    container.querySelectorAll("[data-reject]").forEach((btn) => {
      btn.addEventListener("click", () => onModerate(btn.dataset.reject, "reject"));
    });
  } catch (error) {
    showToast(error.message);
  }
};

const onModerate = async (key, action) => {
  const [contentType, id] = key.split(":");
  const path = contentType === "incident" ? `/api/admin/incidents/${id}/${action}` : `/api/admin/comments/${id}/${action}`;
  try {
    await api(path, {method: "POST", requireAuth: true});
    showToast(`${contentType} ${action}d.`);
    await loadModerationQueue();
  } catch (error) {
    showToast(error.message);
  }
};

/* ---------------- Wire-up ---------------- */

const init = async () => {
  refreshAccountUi();

  const openFileReviewModal = () => {
    document.getElementById("reviewModalTitle").textContent = "File a Review";
    document.getElementById("f-dispute-field").style.display = "none";
    document.getElementById("reviewFormHint").textContent = "Your review is posted publicly once it clears moderation.";
    document.getElementById("reviewSubmitBtn").onclick = () => submitReview({asAdmin: false});
    populateReviewForm();
    setMatchFieldsEditable(true);
    openModal("reviewModalOverlay");
  };
  document.getElementById("fileReviewBtn").addEventListener("click", () => {
    if (!auth.isSignedIn()) {
      openAuthModal(openFileReviewModal);
      return;
    }
    openFileReviewModal();
  });

  document.getElementById("adminPostBtn").addEventListener("click", () => {
    document.getElementById("reviewModalTitle").textContent = "Post as RefRating";
    document.getElementById("f-dispute-field").style.display = "block";
    document.getElementById("reviewFormHint").textContent = "Published immediately as an official RefRating review.";
    document.getElementById("reviewSubmitBtn").onclick = () => submitReview({asAdmin: true});
    populateReviewForm();
    setMatchFieldsEditable(true);
    openModal("reviewModalOverlay");
  });

  document.querySelectorAll("[data-close]").forEach((btn) => {
    btn.addEventListener("click", () => {
      if (btn.dataset.close === "authModalOverlay") pendingPostSignInAction = null;
      closeModal(btn.dataset.close);
    });
  });
  document.querySelectorAll(".modal-overlay").forEach((overlay) => {
    overlay.addEventListener("click", (e) => {
      if (e.target === overlay) {
        if (overlay.id === "authModalOverlay") pendingPostSignInAction = null;
        overlay.classList.remove("open");
      }
    });
  });

  document.getElementById("adminNavLink").addEventListener("click", (e) => {
    e.preventDefault();
    showAdminSection();
  });

  document.querySelectorAll(".chip[data-filter]").forEach((chip) => {
    chip.addEventListener("click", () => {
      document.querySelectorAll(".chip[data-filter]").forEach((c) => c.classList.remove("active"));
      chip.classList.add("active");
      state.disputeFilter = chip.dataset.filter;
      renderTickets();
    });
  });
  document.querySelectorAll(".chip[data-sort]").forEach((chip) => {
    chip.addEventListener("click", async () => {
      document.querySelectorAll(".chip[data-sort]").forEach((c) => c.classList.remove("active"));
      chip.classList.add("active");
      state.sort = chip.dataset.sort;
      state.cursor = undefined;
      await refreshCurrentIncidentsView();
    });
  });

  document.getElementById("clearMatchFilterBtn").addEventListener("click", clearMatchFilter);
  document.getElementById("newIncidentsBanner").addEventListener("click", async () => {
    if (state.currentMatchId) await selectMatch(state.currentMatchId);
  });
  document.getElementById("loadMoreBtn").addEventListener("click", () => loadIncidents({append: true}));

  // Auth modal wiring
  document.getElementById("showSignUp").addEventListener("click", (e) => {
    e.preventDefault();
    showAuthStep("signUp");
  });
  document.getElementById("showSignIn").addEventListener("click", (e) => {
    e.preventDefault();
    showAuthStep("signIn");
  });
  document.getElementById("signInBtn").addEventListener("click", async () => {
    const email = document.getElementById("auth-email").value.trim();
    const password = document.getElementById("auth-password").value;
    try {
      await auth.signIn(email, password);
      closeModal("authModalOverlay");
      refreshAccountUi();
      await loadFollowing();
      renderTickets();
      renderSharedIncident();
      showToast("Signed in.");
      const runAfterSignIn = pendingPostSignInAction;
      pendingPostSignInAction = null;
      if (runAfterSignIn) runAfterSignIn();
    } catch (error) {
      if (String(error.message).includes("UserNotConfirmedException") || /not confirmed/i.test(error.message)) {
        pendingConfirmEmail = email;
        document.getElementById("confirmEmailLabel").textContent = email;
        showAuthStep("confirm");
      } else {
        document.getElementById("authError").textContent = error.message;
      }
    }
  });
  document.getElementById("signUpBtn").addEventListener("click", async () => {
    const email = document.getElementById("signup-email").value.trim();
    const password = document.getElementById("signup-password").value;
    try {
      await auth.signUp(email, password);
      pendingConfirmEmail = email;
      document.getElementById("confirmEmailLabel").textContent = email;
      showAuthStep("confirm");
    } catch (error) {
      document.getElementById("authError").textContent = error.message;
    }
  });
  document.getElementById("confirmBtn").addEventListener("click", async () => {
    const code = document.getElementById("confirm-code").value.trim();
    try {
      await auth.confirmSignUp(pendingConfirmEmail, code);
      showToast("Account confirmed — sign in below.");
      showAuthStep("signIn");
      document.getElementById("auth-email").value = pendingConfirmEmail;
    } catch (error) {
      document.getElementById("authError").textContent = error.message;
    }
  });

  document.getElementById("saveProfileBtn").addEventListener("click", saveProfile);
  document.getElementById("deleteAccountBtn").addEventListener("click", deleteAccount);

  document.getElementById("hamburgerBtn").addEventListener("click", () => {
    const topbar = document.querySelector(".topbar");
    const isOpen = topbar.classList.toggle("mobile-open");
    document.getElementById("hamburgerBtn").setAttribute("aria-expanded", String(isOpen));
  });
  document.querySelectorAll(".topnav a").forEach((link) => {
    link.addEventListener("click", () => document.querySelector(".topbar").classList.remove("mobile-open"));
  });

  document.getElementById("f-team-a").addEventListener("input", updateInfringedTeamOptions);
  document.getElementById("f-team-b").addEventListener("input", updateInfringedTeamOptions);
  wireTeamAutocomplete("f-team-a", "teamADatalist", teamSearchState.teamAIds);
  wireTeamAutocomplete("f-team-b", "teamBDatalist", teamSearchState.teamBIds);
  document.getElementById("f-team-a").addEventListener("change", maybeAutofillMatchDate);
  document.getElementById("f-team-b").addEventListener("change", maybeAutofillMatchDate);

  await loadFollowing();
  await loadSports();
  renderSportSwitcher();
  await Promise.all([loadRules(), loadIncidents(), loadMatches()]);

  const sharedIncidentId = new URLSearchParams(location.search).get("incident");
  if (sharedIncidentId) {
    await showSharedIncident(sharedIncidentId);
  }
};

init().catch((error) => {
  console.error(error);
  showToast(`Failed to load RefRating: ${error.message}`);
});
